if (!customElements.get('wm-product-experience')) {
  customElements.define(
    'wm-product-experience',
    class WmProductExperience extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.sectionId = this.dataset.sectionId;
        this.mobileButton = this.querySelector('[data-wm-mobile-submit]');
        this.mobileButtonText = this.querySelector('[data-wm-mobile-submit-text]');
        this.mobileStatus = this.querySelector('[data-wm-mobile-status]');
        this.currentFit = this.querySelector('[data-wm-current-fit]');
        this.measureDialog = this.querySelector('[data-wm-measure-dialog]');
        this.lastDialogTrigger = null;
        this.selectedVariantId = this.querySelector('.product-variant-id')?.value || null;

        const signal = this.abortController.signal;
        this.querySelectorAll('[data-wm-measure-open]').forEach((trigger) => {
          trigger.addEventListener('click', () => this.openMeasureDialog(trigger), { signal });
        });
        this.querySelectorAll('[data-wm-measure-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeMeasureDialog(), { signal });
        });
        this.measureDialog?.addEventListener('click', (event) => this.handleDialogBackdrop(event), { signal });
        this.measureDialog?.addEventListener('cancel', () => this.restoreDialogFocus(), { signal });
        this.measureDialog?.addEventListener('keydown', (event) => this.trapDialogFocus(event), { signal });
        this.mobileButton?.addEventListener('click', () => this.handleMobileSubmit(), { signal });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.handleVariantChange(event));
        }

        this.syncMobileBar();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribeVariant?.();
      }

      handleVariantChange({ data }) {
        if (String(data?.sectionId) !== String(this.sectionId)) return;

        const variant = data?.variant;
        this.selectedVariantId = variant?.id ? String(variant.id) : null;
        this.updateCurrentFit(variant);
        window.requestAnimationFrame(() => this.syncMobileBar(variant));
      }

      updateCurrentFit(variant) {
        if (!this.currentFit) return;

        const template = variant?.id
          ? this.querySelector(`template[data-wm-fit-variant="${CSS.escape(String(variant.id))}"]`)
          : null;

        if (!template) {
          this.currentFit.replaceChildren();
          this.currentFit.classList.add('hidden');
          return;
        }

        this.currentFit.replaceChildren(template.content.cloneNode(true));
        this.currentFit.classList.remove('hidden');
      }

      syncMobileBar(variant) {
        if (!this.mobileButton) return;

        const mainButton = this.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);
        const sourcePrice = this.querySelector(`#price-${CSS.escape(this.sectionId)} .price`);
        const mobilePrice = this.querySelector('[data-wm-mobile-price]');

        if (sourcePrice && mobilePrice) mobilePrice.replaceChildren(sourcePrice.cloneNode(true));

        if (variant === null || this.selectedVariantId === null) {
          this.mobileButton.disabled = false;
          this.mobileButtonText.textContent = this.mobileButton.dataset.chooseLabel;
          return;
        }

        const isUnavailable = variant ? variant.available === false : mainButton?.disabled;
        this.mobileButton.disabled = Boolean(isUnavailable);
        this.mobileButtonText.textContent = isUnavailable
          ? this.mobileButton.dataset.soldOutLabel
          : this.mobileButton.dataset.addLabel;
      }

      handleMobileSubmit() {
        const mainButton = this.querySelector(`#ProductSubmitButton-${CSS.escape(this.sectionId)}`);

        if (!this.selectedVariantId) {
          if (this.mobileStatus) this.mobileStatus.textContent = this.mobileButton.dataset.selectionMessage;
          this.querySelector('variant-selects input:not([disabled]), variant-selects select:not([disabled])')?.focus();
          return;
        }

        if (!mainButton || mainButton.disabled) return;
        if (this.mobileStatus) this.mobileStatus.textContent = '';
        mainButton.click();
      }

      openMeasureDialog(trigger) {
        if (!this.measureDialog) return;
        this.lastDialogTrigger = trigger;

        if (typeof this.measureDialog.showModal === 'function') {
          this.measureDialog.showModal();
        } else {
          this.measureDialog.setAttribute('open', '');
        }

        this.measureDialog.querySelector('[data-wm-measure-close]')?.focus();
      }

      closeMeasureDialog() {
        if (!this.measureDialog) return;

        if (typeof this.measureDialog.close === 'function') {
          this.measureDialog.close();
        } else {
          this.measureDialog.removeAttribute('open');
        }

        this.restoreDialogFocus();
      }

      handleDialogBackdrop(event) {
        if (event.target === this.measureDialog) this.closeMeasureDialog();
      }

      restoreDialogFocus() {
        window.requestAnimationFrame(() => this.lastDialogTrigger?.focus());
      }

      trapDialogFocus(event) {
        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          this.measureDialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => element.offsetParent !== null);

        if (focusable.length === 0) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
